/*
Author: Chathuraka Mallawa Arachchi
Date: 18/10/2024
Copyright © 2024 Infix!. All rights reserved.

Responsible for managing frm state including validations.
*/

import { patientQuestionnaireDetails } from '@/components/survey/SurveyModel'
import { FormState } from '@/components/survey/SurveyModel/types/commonTypes'
import { INode } from '@/components/survey/SurveyModel/types/nodeTypes'
import {
    PatientAnswerObj,
    PatientStatusObj,
} from '@/components/survey/SurveyModel/types/patientTypes'
import { Connection } from '@/utils'
import { createSlice, current, PayloadAction } from '@reduxjs/toolkit'
import { useDispatch } from 'react-redux'

export interface IPrevPage {
    page: string
    prevPage: string
}
export interface IpatientSurveyState {
    currentPage: string
    patientQuestionnaire?: INode
    selectedNode?: INode
    questionnaireDetails?: INode
    currentQuestion?: INode
    prevPages?: IPrevPage[]
    answersObj: PatientAnswerObj | null
    patient: any
    QuestionnaireStatus: PatientStatusObj | null
}

const initialState: IpatientSurveyState = {
    currentPage: '',
    patientQuestionnaire: undefined,
    selectedNode: undefined,
    questionnaireDetails: undefined,
    currentQuestion: undefined,
    prevPages: [],
    answersObj: null,
    patient: {},
    QuestionnaireStatus: null,
}

const patientSurveySlice = createSlice({
    name: 'patientSurvey',
    initialState,
    reducers: {
        setCurrentPage: (
            state,
            action: PayloadAction<{
                value: string
            }>
        ) => {
            const { value } = action.payload
            state.currentPage = value
        },
        setPrevPage: (
            state,
            action: PayloadAction<{
                page: string //page.nextpage
                prevPage: string //page .name
            }>
        ) => {
            const { page, prevPage } = action.payload
            const existingIndex = state.prevPages?.findIndex(
                (historyItem) => historyItem.page === page
            )

            if (
                state.prevPages &&
                existingIndex !== -1 &&
                existingIndex !== undefined
            ) {
                state.prevPages[existingIndex].prevPage = prevPage
            } else {
                state.prevPages?.push({ page, prevPage })
            }
        },

        patientSurveyFromDB: (
            state,
            action: PayloadAction<{
                nodes: INode
            }>
        ) => {
            const { nodes } = action.payload
            state.questionnaireDetails = nodes
        },
        patientDetails: (
            state,
            action: PayloadAction<{
                patient: any
            }>
        ) => {
            const { patient } = action.payload
            state.patient = patient
        },

        patientSurveyState: (
            /* */
            state,
            action: PayloadAction<INode | undefined>
        ) => {
            state.patientQuestionnaire = action.payload
                ? { ...action.payload }
                : undefined
        },
        setQUestionAnswers: (
            state,
            action: PayloadAction<{ answers: PatientAnswerObj | null }>
        ) => {
            const { answers } = action.payload
            state.answersObj = answers
        },
        setQuestionnaireStatus: (
            state,
            action: PayloadAction<{ status: PatientStatusObj | null }>
        ) => {
            const { status } = action.payload
            state.QuestionnaireStatus = status
        },
        patientSetCurrentNode: (
            /*Current active Node*/
            state,
            action: PayloadAction<{ page: string; form: FormState }>
        ) => {
            const { page, form } = action.payload
            let section = null
            let currentNode = null
            const copyPatientQuestionnaire = structuredClone(
                current(state.questionnaireDetails)
            )

            if (copyPatientQuestionnaire) {
                section = patientQuestionnaireDetails.findNearestSection(
                    copyPatientQuestionnaire,
                    page
                )
                currentNode = section
                    ? patientQuestionnaireDetails.findCurrentNode(section, page)
                    : null
                //applyNextPrevLinksToANode = (node: INode, activeNode: INode, sectionName: string, form: FormState  )

                if (section && currentNode) {
                    currentNode =
                        patientQuestionnaireDetails.applyNextPrevLinksToANode(
                            copyPatientQuestionnaire,
                            currentNode,
                            section.name,
                            form
                        )

                    //setLabelText(label)
                    if (currentNode.title && state?.answersObj) {
                        currentNode.title =
                            patientQuestionnaireDetails.processText(
                                currentNode.title,
                                state?.answersObj.answers
                            )
                    }
                    section.nodes = [currentNode]
                    copyPatientQuestionnaire.nodes = [section]
                    state.currentQuestion = currentNode
                }
            }
            state.selectedNode = copyPatientQuestionnaire
        },
    },
})

export const {
    setCurrentPage,
    patientSurveyState,
    patientSetCurrentNode,
    patientSurveyFromDB,
    setPrevPage,
    setQUestionAnswers,
    setQuestionnaireStatus,
    patientDetails,
} = patientSurveySlice.actions

export const useGetPatientSurvey = () => {
    const dispatch = useDispatch()

    const getQuestionnaire = async (id: number) => {
        try {
            // Call your API to get the answers
            const response = await fetch(
                Connection.baseUrl('getQuestionnaire') +
                    `?id=${id}&definitionTypeId=2`,
                {
                    method: 'GET',
                    headers: Connection.headers(),
                }
            )

            if (!response.ok) {
                throw new Error(
                    `Error: ${response.status} - ${response.statusText}`
                )
            }

            const definition = await response.json()
            const nodes = definition.questionnaireDefinition.definitionJson

            // Dispatch the answers to Redux
            //dispatch(setQUestionAnswers({ answers }))
            dispatch(patientSurveyFromDB({ nodes }))
        } catch (error) {
            console.error('Failed to fetch question answers:', error)
        }
    }

    return getQuestionnaire
}
export const useSetCurrentPage = () => {
    const dispatch = useDispatch()

    const updateCurrentPage = (value: string) => {
        dispatch(setCurrentPage({ value }))
    }

    return updateCurrentPage
}

export const useSetPrevPage = () => {
    const dispatch = useDispatch()

    const updatePrevPage = (page: string, prevPage: string) => {
        dispatch(setPrevPage({ page, prevPage }))
    }

    return updatePrevPage
}

export const useSetSurveyState = () => {
    const dispatch = useDispatch()

    const setSurveyState = (value: INode) => {
        dispatch(patientSurveyState(value))
    }

    return setSurveyState
}

export const useSetCurrentNode = () => {
    const dispatch = useDispatch()

    const setCurrentNode = (page: string, form: FormState) => {
        dispatch(patientSetCurrentNode({ page, form }))
    }

    return setCurrentNode
}

export const useGetQUestionAnswersReset = () => {
    const dispatch = useDispatch()

    const getQuestionnaireReset = async () => {
        dispatch(setQUestionAnswers({ answers: null }))
    }

    return getQuestionnaireReset
}

export const useGetQUestionAnswers = () => {
    const dispatch = useDispatch()

    const getQuestionnaire = async (id: number) => {
        try {
            // Call your API to get the answers
            const response = await fetch(
                Connection.baseUrl('QuestionnaireAnswers') +
                    `?questionnaireResponseId=${id}`,
                {
                    method: 'GET',
                    headers: Connection.headers(),
                }
            )

            if (!response.ok) {
                throw new Error(
                    `Error: ${response.status} - ${response.statusText}`
                )
            }

            const answers = await response.json()

            // Dispatch the answers to Redux
            dispatch(setQUestionAnswers({ answers }))
        } catch (error) {
            console.error('Failed to fetch question answers:', error)
        }
    }

    return getQuestionnaire
}

export const useGetPatientDetails = () => {
    const dispatch = useDispatch()

    const getPatientDetails = async (id: number) => {
        try {
            // Call your API to get the answers
            const response = await fetch(
                Connection.baseUrl('getPatient') + `?id=${id}`,
                {
                    method: 'GET',
                    headers: Connection.headers(),
                }
            )

            if (!response.ok) {
                throw new Error(
                    `Error: ${response.status} - ${response.statusText}`
                )
            }

            const patient = await response.json()

            // Dispatch the answers to Redux
            dispatch(patientDetails({ patient }))
        } catch (error) {
            console.error('Failed to fetch question answers:', error)
        }
    }

    return getPatientDetails
}

export const useGetQuestionnaireStatusReset = () => {
    const dispatch = useDispatch()

    const getQuestionnaireStatusReset = async () => {
        dispatch(setQuestionnaireStatus({ status: null }))
    }

    return getQuestionnaireStatusReset
}

export const useGetQuestionnaireStatus = () => {
    const dispatch = useDispatch()
    // getQuestionnaireStatus: `${root}/waitinglistrecords`, //waitinglistrecords/41/questionnaireresponses/5
    const getQuestionnaireStatus = async (
        waitinglistrecord: number,
        questionnaireresponse: number
    ) => {
        try {
            // Call your API to get the answers
            const response = await fetch(
                Connection.baseUrl('getQuestionnaireStatus') +
                    `/${waitinglistrecord}/questionnaireresponses/${questionnaireresponse}`,
                {
                    method: 'GET',
                    headers: Connection.headers(),
                }
            )

            if (!response.ok) {
                throw new Error(
                    `Error: ${response.status} - ${response.statusText}`
                )
            }

            const status = await response.json()

            // Dispatch the answers to Redux
            dispatch(setQuestionnaireStatus({ status }))
        } catch (error) {
            console.error('Failed to fetch questionnair status:', error)
        }
    }

    return getQuestionnaireStatus
}

export default patientSurveySlice.reducer
